import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/* Context — any button on the site can call useQuoteModal().open().
   The heavy wizard UI lives in ./quote-wizard and is only downloaded the
   first time a visitor actually opens the modal. */

const QuoteModal = lazy(() =>
  import("./quote-wizard").then((m) => ({ default: m.QuoteModal })),
);

type QuoteModalCtx = { open: () => void; close: () => void; isOpen: boolean };
const Ctx = createContext<QuoteModalCtx | null>(null);

export function useQuoteModal() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useQuoteModal must be used inside <QuoteModalProvider>");
  return c;
}

export type QuotePrefill = {
  services?: string[];
  message?: string;
  /** Home Care membership plan name, forwarded with the lead. */
  plan?: string;
};

let _pendingPrefill: QuotePrefill | null = null;
/** Read-and-clear the prefill queued by the button that opened the modal. */
export function consumeQuotePrefill(): QuotePrefill | null {
  const p = _pendingPrefill;
  _pendingPrefill = null;
  return p;
}

// Imperative escape hatch so non-React code (or buttons rendered outside the
// provider tree) can open the modal. Set by the provider on mount.
let _openQuoteModal: (() => void) | null = null;
export function openQuoteModal(prefill?: QuotePrefill) {
  _pendingPrefill = prefill ?? null;
  _openQuoteModal?.();
}

// Warm the wizard chunk once the browser is idle so the first open is instant.
let warmed = false;
export function prefetchQuoteModal() {
  if (warmed) return;
  warmed = true;
  void import("./quote-wizard");
}

export function QuoteModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const open = useCallback(() => {
    prefetchQuoteModal();
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    _openQuoteModal = open;
    return () => {
      if (_openQuoteModal === open) _openQuoteModal = null;
    };
  }, [open]);

  useEffect(() => {
    const warm = () => prefetchQuoteModal();
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    const t = ric ? ric(warm) : window.setTimeout(warm, 2500);
    return () => {
      if (!ric) window.clearTimeout(t as number);
    };
  }, []);

  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {isOpen && (
        <Suspense fallback={null}>
          <QuoteModal onClose={close} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}
